/**
 * Supabase image-transform helper.
 *
 * Card avatars etc. are stored at capture resolution (~700-1600px) but
 * display at 80-660px — PageSpeed flagged ~2-3MB of oversized images per
 * page. Supabase's render endpoint resizes on the fly:
 *   /storage/v1/object/public/...  ->  /storage/v1/render/image/public/...
 *
 * CRITICAL: the endpoint does NOT preserve aspect ratio with width-only
 * (920x1608 + ?width=160 returned a squished 160x1608!), so we always send
 * a square bounding box with resize=contain: the image is scaled to fit
 * within box x box with its aspect ratio intact, no crop, no padding
 * (920x1608 + box 160 -> 92x160). `box` should be the largest displayed
 * dimension x2 for retina.
 *
 * Non-Supabase URLs (local /images, external CDNs) pass through untouched.
 * Videos are never transformed.
 */

const OBJECT_PATH = '/storage/v1/object/public/';
const RENDER_PATH = '/storage/v1/render/image/public/';

export function transformedImageUrl(
  url: string | null | undefined,
  box: number,
  quality = 75,
): string | null {
  if (!url) return null;
  if (!url.includes(OBJECT_PATH)) return url;
  // Don't touch videos or already-transformed URLs.
  if (/\.(mp4|webm|mov)(\?|$)/i.test(url)) return url;

  const [base, existingQuery] = url.split('?');
  const rewritten = base!.replace(OBJECT_PATH, RENDER_PATH);
  const params = new URLSearchParams(existingQuery ?? '');
  params.set('width', String(box));
  params.set('height', String(box));
  params.set('resize', 'contain');
  params.set('quality', String(quality));
  return `${rewritten}?${params.toString()}`;
}
